import { Trash2 } from "lucide-react";
import {
  Form,
  minValue,
  required,
  useCreate,
  useDelete,
  useGetIdentity,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";

import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { FileInput } from "@/components/admin/file-input";
import { SaveButton } from "@/components/admin/form";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { contactOptionText } from "../../misc/ContactOption";
import { AttachmentField } from "../../notes/AttachmentField";
import type { Sale, Task } from "../../types";
import { DEFAULT_PRIORITY, PRIORITIES } from "../cardModel";
import { newItemPosition } from "../positions";
import { TASK_STATUSES } from "./taskStatuses";

const RESOURCE = "tasks";

export interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  /** Task to edit; when absent the dialog creates a task. */
  task?: Task | null;
  /** Initial values of a new task (e.g. the card it belongs to, its status). */
  defaults?: Partial<Task>;
}

const saleOptionText = (sale: Sale) => `${sale.first_name} ${sale.last_name}`;

const toNullableNumber = (value: unknown) =>
  value === "" || value == null || Number.isNaN(Number(value))
    ? null
    : Number(value);

export const TaskDialog = ({
  open,
  onClose,
  task,
  defaults,
}: TaskDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();

  const statusChoices = TASK_STATUSES.map((id) => ({
    id,
    name: `crm.operations.tasks.statuses.${id}`,
  }));
  const priorityChoices = PRIORITIES.map((id) => ({
    id,
    name: `crm.operations.priorities.${id}`,
  }));

  const record = useMemo(
    () =>
      task ?? {
        text: "",
        description: "",
        type: "none",
        status: "todo",
        priority: DEFAULT_PRIORITY,
        sales_id: identity?.id,
        contact_id: null,
        due_date: null,
        estimated_hours: null,
        spent_hours: null,
        attachments: [],
        ...defaults,
      },
    [task, defaults, identity?.id],
  );

  const onError = (error: unknown) =>
    notify(
      error instanceof Error ? error.message : "ra.notification.http_error",
      { type: "error" },
    );

  const handleSubmit = async (values: FieldValues) => {
    const data = {
      text: values.text,
      description: values.description || null,
      status: values.status,
      priority: values.priority,
      sales_id: values.sales_id,
      contact_id: values.contact_id || null,
      due_date: values.due_date || null,
      estimated_hours: toNullableNumber(values.estimated_hours),
      spent_hours: toNullableNumber(values.spent_hours),
      attachments: values.attachments ?? [],
    };
    if (task) {
      await update(
        RESOURCE,
        { id: task.id, data, previousData: task },
        {
          onSuccess: () => {
            notify("crm.operations.tasks.updated");
            onClose();
          },
          onError,
        },
      );
      return;
    }
    await create(
      RESOURCE,
      { data: { ...data, type: "none", position: newItemPosition() } },
      {
        onSuccess: () => {
          notify("crm.operations.tasks.created");
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="lg:max-w-2xl overflow-y-auto max-h-9/10">
        {open && (
          <Form
            record={record}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {translate(
                  task
                    ? "crm.operations.tasks.edit"
                    : "crm.operations.tasks.create",
                )}
              </DialogTitle>
            </DialogHeader>
            <TextInput
              source="text"
              label="crm.operations.tasks.fields.title"
              validate={required()}
              helperText={false}
              autoFocus
            />
            <TextInput
              source="description"
              label="crm.operations.tasks.fields.description"
              multiline
              helperText={false}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SelectInput
                source="status"
                label="crm.operations.tasks.fields.status"
                choices={statusChoices}
                validate={required()}
                helperText={false}
              />
              <SelectInput
                source="priority"
                label="crm.operations.tasks.fields.priority"
                choices={priorityChoices}
                validate={required()}
                helperText={false}
              />
              <ReferenceInput
                source="sales_id"
                reference="sales"
                sort={{ field: "first_name", order: "ASC" }}
                filter={{ "disabled@neq": true }}
              >
                <SelectInput
                  label="crm.operations.tasks.fields.assignee"
                  optionText={saleOptionText}
                  validate={required()}
                  helperText={false}
                />
              </ReferenceInput>
              <ReferenceInput source="contact_id" reference="contacts_summary">
                <AutocompleteInput
                  label="crm.operations.tasks.fields.card"
                  optionText={contactOptionText}
                  helperText={false}
                  modal
                />
              </ReferenceInput>
              <DateTimeInput
                source="due_date"
                label="crm.operations.tasks.fields.due_date"
                helperText={false}
              />
              <div className="grid grid-cols-2 gap-4">
                <NumberInput
                  source="estimated_hours"
                  label="crm.operations.tasks.fields.estimated_hours"
                  min={0}
                  step={0.5}
                  validate={minValue(0)}
                  helperText={false}
                />
                <NumberInput
                  source="spent_hours"
                  label="crm.operations.tasks.fields.spent_hours"
                  min={0}
                  step={0.5}
                  validate={minValue(0)}
                  helperText={false}
                />
              </div>
            </div>
            <FileInput
              source="attachments"
              label="crm.operations.tasks.fields.attachments"
              multiple
            >
              <AttachmentField source="src" title="title" target="_blank" />
            </FileInput>
            <DialogFooter className="w-full gap-2 sm:justify-between">
              {task ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive"
                  disabled={isDeleting}
                  onClick={() =>
                    deleteOne(
                      RESOURCE,
                      { id: task.id, previousData: task },
                      {
                        onSuccess: () => {
                          notify("crm.operations.tasks.deleted");
                          onClose();
                        },
                        onError,
                      },
                    )
                  }
                >
                  <Trash2 />
                  {translate("crm.operations.tasks.delete")}
                </Button>
              ) : (
                <span />
              )}
              <SaveButton />
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
